<template>
  <v-card>
    <v-card-title>
      {{ $t('payment.milestone.partsTitle', { name: payment.name }) }}
    </v-card-title>
    <v-card-text>
      <v-row>
        <v-col cols="3">
          <ChartPie
            :items="itemsForChart"
            :sizes="{ lg: 250, md: 200 }"
            :width="25"
            hide-legend
            hide-summary
          />
        </v-col>
        <v-col>
          <v-row>
            <p
              class="text-small"
              v-html="remainingText"
            />
          </v-row>
          <SubMilestoneEditRow
            v-for="(v, index) in $v.form.$each.$iter"
            :validate="v"
            :payment-name="payment.name"
            :key="index"
            v-model="items[index]"
            :color="items[index].color"
            @input="handleRowChange"
            :payment-payment-amount="payment.paymentAmountCents"
            :percent-to-cover="percentToCover"
            :contract-price="contractPrice"
            :is-variation="!!payment.variationId"
            :nameDuplicateError="nameDuplicateError"
            :currency="{ key: payment.currency.key || payment.currency, value: 1 }"
          />
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        rounded
        dark
        dense
        outlined
        color="primary"
        @click="handleAddPart"
        v-if="amountToCover > 0"
      >
      <v-icon>mdi-plus</v-icon>
      {{ $t('payment.milestone.addPart') }}
      </v-btn>
      <v-btn
        rounded
        dark
        dense
        outlined
        color="primary"
        @click="cancel"
      >
        {{ $t('common.cancel') }}
      </v-btn>
      <SubmitButton @click="submit" :disabled="!items.length" />
    </v-card-actions>
  </v-card>
</template>

<script>
import colors from 'vuetify/lib/util/colors'
import { subPaymentMilestonesModel } from '@/validators'
import cloneDeep from 'lodash/cloneDeep'
import ColorsMixin from '@/mixins/ColorsMixin'
import { calculateApplicationMilestoneValues } from '@/services'
import { EMPTY_PAYMENT_SUBMILESTONE } from '@/helpers'

export default {
  name: 'SubMilestonesPercentForm',
  props: {
    payment: {
      type: Object,
      default: () => ({})
    },
    maxWidth: {
      type: Number,
      default: 1000
    },
    contractPrice: Object,
    paymentApplications: Array,
    currencies: Array,
    chosenCurrency: Object
  },
  mixins: [ColorsMixin],
  data () {
    return {
      form: this.payment.subMilestones ? cloneDeep(this.payment.subMilestones) : []
    }
  },
  computed: {
    amountToCover () {
      return Math.round(this.payment.paymentAmountCents -
        this.form.reduce((acc, curr) => acc + Number(curr.paymentAmountCents), 0))
    },
    percentToCover () {
      return Math.round((this.amountToCover / this.payment.paymentAmountCents * 100) * 100) / 100
    },
    remainingText () {
      if (this.amountToCover > 0) {
        return this.$t('payment.milestone.partsNotCover', { remainingValue: this.$options.filters.formatCurrency(this.amountToCover), percent: this.percentToCover })
      } else if (this.amountToCover < 0) {
        return this.$t('payment.milestone.partsCoverMore', { percent: this.$options.filters.toFixed(this.percentToCover * -1 + 100) })
      } else {
        return ''
      }
    },
    itemsForChart () {
      const amount = this.payment.paymentAmountCents - this.items.reduce((acc, curr) => acc + Number(curr.paymentAmountCents), 0)
      if (amount > 0) {
        return [...this.items, {
          ...EMPTY_PAYMENT_SUBMILESTONE,
          value: amount / this.payment.paymentAmountCents * 100,
          color: colors.grey.lighten2
        }]
      }
      return this.items
    },
    items () {
      return this.form.map((item, index) => {
        return {
          ...item,
          index,
          value: item.paymentAmountCents / this.payment.paymentAmountCents * 100,
          color: this.colors[index % this.colors.length],
          ...calculateApplicationMilestoneValues(this.paymentApplications, item, this.currencies)
        }
      })
    }
  },
  methods: {
    nameDuplicateError (name) {
      const names = this.form.filter(i => i.name === name)
      return names.length > 1 ? this.$t('payment.milestone.isDuplicated') : null
    },
    handleRowChange (item) {
      Object.assign(
        this.form[item.index],
        item
      )
    },
    handleAddPart () {
      const maxLen = 10 - `.${this.form.length + 1}`.length
      const name = `${this.payment.name.substring(0, maxLen)}.${this.form.length + 1}`
      this.form.push({
        ...EMPTY_PAYMENT_SUBMILESTONE,
        amountAssignedToApplication: 0,
        amountPaid: 0,
        index: this.form.length,
        name,
        paymentAmountCents: this.amountToCover,
        percentOfMilestone: this.percentToCover,
        percentOfContractPrice: this.payment.variationId ? 0 : (this.amountToCover / this.contractPrice) * 100
      })
    },
    cancel () {
      this.form = this.payment.subMilestones ? cloneDeep(this.payment.subMilestones) : []
      this.$v.form.$reset()
      this.$emit('cancel')
      this.$eventBus.$emit('CLOSE_MODAL')
    },
    async submit () {
      this.$v.form.$touch()
      if (!this.$v.form.$invalid) {
        this.$emit('saveSubPayments', this.form)
        this.$eventBus.$emit('CLOSE_MODAL')
        this.$v.form.$reset()
        this.form = this.payment.subMilestones ? cloneDeep(this.payment.subMilestones) : []
      }
    }
  },
  watch: {
    payment (v) {
      this.form = cloneDeep(v.subMilestones)
    }
  },
  validations: subPaymentMilestonesModel
}
</script>
